import React from 'react';

interface VoiceInteractionPickerProps {
  mode: 'text' | 'voice';
  voiceGender: 'male' | 'female' | null;
  onModeChange: (m: 'text' | 'voice') => void;
  onVoiceGenderChange: (g: 'male' | 'female') => void;
}

/**
 * Shared interaction-mode + voice gender selector. Renders Text Only / Voice mode cards and,
 * in Voice mode, Male / Female buttons. The backend resolves the gender to the configured
 * default voice ID via default_male_voice / default_female_voice catalogue settings.
 * Used by both the user-onboarding voice step and the SAM settings tab.
 */
const VoiceInteractionPicker: React.FC<VoiceInteractionPickerProps> = ({
  mode,
  voiceGender,
  onModeChange,
  onVoiceGenderChange,
}) => {
  const ModeCard: React.FC<{ value: 'text' | 'voice'; title: string; desc: string }> = ({ value, title, desc }) => (
    <button
      type="button"
      onClick={() => onModeChange(value)}
      className={`flex-1 text-left border rounded-xl p-4 transition ${
        mode === value ? 'border-accent ring-2 ring-ui-ring/30 bg-accent-soft/40' : 'border-ui-border hover:border-ui-border3'
      }`}
    >
      <p className="text-sm font-semibold text-ui-text">{title}</p>
      <p className="text-xs text-ui-text2 mt-1">{desc}</p>
    </button>
  );

  return (
    <div>
      <div className="flex gap-3 mb-6">
        <ModeCard value="text" title="Text Only" desc="Type to chat with SAM." />
        <ModeCard value="voice" title="Voice & Text" desc="Talk to SAM and hear replies." />
      </div>

      {mode === 'voice' && (
        <div>
          <label className="block text-sm font-medium text-ui-text mb-2">Choose a voice</label>
          <div className="flex gap-3">
            {(['female', 'male'] as const).map((gender) => (
              <button
                key={gender}
                type="button"
                onClick={() => onVoiceGenderChange(gender)}
                className={`flex-1 border rounded-xl py-4 text-sm font-semibold capitalize transition ${
                  voiceGender === gender
                    ? 'border-accent ring-2 ring-ui-ring/30 bg-accent-soft/40 text-ui-text'
                    : 'border-ui-border hover:border-ui-border3 text-ui-text'
                }`}
              >
                {gender}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInteractionPicker;
